import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import {
  GitBranch, RefreshCw, Download, ExternalLink, Loader2, Search,
  FolderGit2, Code2, Clock, Eye, Trash2, Settings, Globe, CheckCircle,
  AlertTriangle, Lock, Box, Plus, Camera, Upload, Image, X, Monitor
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const LANG_COLORS = {
  JavaScript: "#f1e05a", TypeScript: "#3178c6", Python: "#3572A5", Dart: "#00B4AB",
  HTML: "#e34c26", CSS: "#563d7c", Shell: "#89e051", Java: "#b07219", Go: "#00ADD8",
  Rust: "#dea584", Ruby: "#701516", PHP: "#4F5D95", "C#": "#178600", Swift: "#F05138",
  React: "#61DAFB", FastAPI: "#009688", MongoDB: "#47A248", Vue: "#4FC08D",
};

const STATUS_MAP = {
  development: { label: "Developpement", color: "bg-[#F59E0B]/10 text-[#F59E0B]", icon: Code2 },
  staging: { label: "Staging", color: "bg-[#8B5CF6]/10 text-[#8B5CF6]", icon: Eye },
  production: { label: "Production", color: "bg-[#10B981]/10 text-[#10B981]", icon: Globe },
  archived: { label: "Archive", color: "bg-[#64748B]/10 text-[#64748B]", icon: Box },
};

const SCREENSHOT_STATUS = {
  pending: { label: "En attente", color: "text-[#94A3B8]", icon: Clock },
  capturing: { label: "En cours", color: "text-[#F59E0B]", icon: Loader2 },
  ready: { label: "Terminee", color: "text-[#10B981]", icon: CheckCircle },
  failed: { label: "Echouee", color: "text-[#EF4444]", icon: AlertTriangle },
};

const PROJECT_TYPES = [
  { value: "site_web", label: "Site Web" },
  { value: "application_web", label: "Application Web" },
  { value: "application_mobile", label: "Application Mobile" },
  { value: "e_commerce", label: "E-Commerce" },
  { value: "landing_page", label: "Landing Page" },
  { value: "saas", label: "SaaS" },
  { value: "autre", label: "Autre" },
];

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `il y a ${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `il y a ${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `il y a ${days}j`;
  return `il y a ${Math.floor(days / 30)} mois`;
}

export default function ProjectControlCenter() {
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState("projects");
  const [repos, setRepos] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(null);
  const [importing, setImporting] = useState(null);
  const [search, setSearch] = useState("");
  const [detailDialog, setDetailDialog] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [editProject, setEditProject] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [screenshotDialog, setScreenshotDialog] = useState(null);
  const [capturingScreenshot, setCapturingScreenshot] = useState(null);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncLogs, setSyncLogs] = useState([]);
  const [showSyncLogs, setShowSyncLogs] = useState(false);
  const [triggeringSync, setTriggeringSync] = useState(false);

  useEffect(() => {
    fetchProjects();
    fetchClients();
    fetchSyncStatus();
  }, []);

  const fetchProjects = async () => {
    setProjectsLoading(true);
    try {
      const res = await axios.get(`${API}/github/projects`);
      setProjects(res.data);
    } catch { /* ignore */ }
    finally { setProjectsLoading(false); }
  };

  const fetchRepos = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/github/repos`);
      setRepos(res.data.repos || []);
      if (res.data.total === 0) {
        toast.info("Aucun depot trouve. Verifiez les permissions du token GitHub.");
      } else {
        toast.success(`${res.data.total} depots trouves`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur chargement depots");
    } finally { setLoading(false); }
  };

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API}/clients`);
      setClients(res.data);
    } catch { /* ignore */ }
  };

  const fetchSyncStatus = async () => {
    try {
      const res = await axios.get(`${API}/github/projects/kpi`);
      setSyncStatus(res.data.last_auto_sync);
    } catch { /* ignore */ }
  };

  const fetchSyncLogs = async () => {
    try {
      const res = await axios.get(`${API}/github/sync/logs`);
      setSyncLogs(res.data);
      setShowSyncLogs(true);
    } catch { toast.error("Erreur chargement logs"); }
  };

  const handleSyncAll = async () => {
    setTriggeringSync(true);
    try {
      await axios.post(`${API}/github/sync/now`);
      toast.success("Synchronisation globale lancee");
      setTimeout(() => { fetchProjects(); fetchSyncStatus(); setTriggeringSync(false); }, 8000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur sync");
      setTriggeringSync(false);
    }
  };

  const handleImport = async (repo) => {
    setImporting(repo.name);
    try {
      await axios.post(`${API}/github/repos/${repo.name}/import`, {});
      toast.success(`${repo.name} importe avec succes`);
      fetchProjects();
      fetchRepos();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur import");
    } finally { setImporting(null); }
  };

  const handleSync = async (projectId) => {
    setSyncing(projectId);
    try {
      await axios.post(`${API}/github/projects/${projectId}/sync`);
      toast.success("Synchronisation terminee");
      fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur sync");
    } finally { setSyncing(null); }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm("Retirer ce projet du Control Center ?")) return;
    try {
      await axios.delete(`${API}/github/projects/${projectId}`);
      toast.success("Projet retire");
      fetchProjects();
    } catch { toast.error("Erreur suppression"); }
  };

  const showDetails = async (repoName) => {
    setDetailDialog(repoName);
    setDetailLoading(true);
    setDetailData(null);
    try {
      const res = await axios.get(`${API}/github/repos/${repoName}/details`);
      setDetailData(res.data);
    } catch { toast.error("Erreur chargement details"); }
    finally { setDetailLoading(false); }
  };

  const handleUpdateProject = async (projectId, updates) => {
    try {
      await axios.put(`${API}/github/projects/${projectId}`, updates);
      toast.success("Projet mis a jour");
      setEditProject(null);
      fetchProjects();
    } catch { toast.error("Erreur mise a jour"); }
  };

  const handleTriggerScreenshot = async (projectId) => {
    setCapturingScreenshot(projectId);
    try {
      await axios.post(`${API}/github/projects/${projectId}/screenshot`);
      toast.success("Capture en cours...");
      // Poll for status
      setTimeout(() => { fetchProjects(); setCapturingScreenshot(null); }, 5000);
      setTimeout(fetchProjects, 15000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur capture");
      setCapturingScreenshot(null);
    }
  };

  const handleManualCreate = async (data) => {
    try {
      await axios.post(`${API}/github/projects/manual`, data);
      toast.success("Projet cree avec succes");
      setShowCreateDialog(false);
      fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur creation");
    }
  };

  const filteredRepos = repos.filter(r =>
    !search || r.name.toLowerCase().includes(search.toLowerCase()) ||
    (r.language || "").toLowerCase().includes(search.toLowerCase())
  );

  const filteredProjects = projects.filter(p =>
    !search || (p.project_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (p.repo_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (p.client_name || "").toLowerCase().includes(search.toLowerCase())
  );

  if (!isAdmin) return null;

  return (
    <div className="space-y-6" data-testid="project-control-center">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <FolderGit2 className="h-6 w-6 text-[#0099CC]" />
            Project Control Center
          </h1>
          <p className="text-[#94A3B8] mt-1">Centralisez la gestion de vos applications</p>
        </div>
        <div className="flex items-center gap-2">
          {syncStatus?.completed_at && (
            <span className="text-xs text-[#64748b] mr-2 hidden md:inline" data-testid="last-sync-time">
              Derniere sync: {new Date(syncStatus.completed_at).toLocaleString("fr-FR", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" })}
            </span>
          )}
          <Button size="sm" variant="outline" onClick={fetchSyncLogs}
            className="border-[#2A3441] text-[#94A3B8] hover:text-white" data-testid="sync-logs-btn">
            <Clock className="h-4 w-4 mr-1" /> Logs
          </Button>
          <Button size="sm" onClick={handleSyncAll} disabled={triggeringSync}
            className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid="sync-all-btn">
            <RefreshCw className={`h-4 w-4 mr-1 ${triggeringSync ? "animate-spin" : ""}`} />
            Synchroniser tout
          </Button>
          <Button onClick={() => setShowCreateDialog(true)}
            className="bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="create-project-btn">
            <Plus className="h-4 w-4 mr-2" /> Nouveau projet
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-[#151B2B] rounded-lg p-1 w-fit" data-testid="pcc-tabs">
        <button onClick={() => setTab("projects")}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === "projects" ? "bg-[#0099CC] text-white" : "text-[#94A3B8] hover:text-white"}`}
          data-testid="tab-projects">
          Mes Projets ({projects.length})
        </button>
        <button onClick={() => { setTab("discover"); if (repos.length === 0) fetchRepos(); }}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === "discover" ? "bg-[#0099CC] text-white" : "text-[#94A3B8] hover:text-white"}`}
          data-testid="tab-discover">
          Depots GitHub
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#64748b]" />
        <Input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Rechercher un projet, depot ou client..."
          className="pl-10 bg-[#151B2B] border-[#2A3441] text-white" data-testid="pcc-search" />
      </div>

      {/* PROJECTS TAB */}
      {tab === "projects" && (
        <div className="space-y-3">
          {projectsLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-[#0099CC]" />
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-16 bg-[#151B2B] rounded-xl border border-[#2A3441]">
              <FolderGit2 className="h-12 w-12 text-[#64748b] mx-auto mb-3" />
              <p className="text-[#94A3B8] mb-2">Aucun projet importe</p>
              <p className="text-[#64748b] text-sm mb-4">Importez depuis GitHub ou creez manuellement</p>
              <div className="flex gap-3 justify-center">
                <Button onClick={() => { setTab("discover"); fetchRepos(); }}
                  variant="outline" className="border-[#2A3441] text-[#94A3B8] hover:text-white" data-testid="discover-btn">
                  <GitBranch className="h-4 w-4 mr-2" /> Depots GitHub
                </Button>
                <Button onClick={() => setShowCreateDialog(true)}
                  className="bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="create-first-btn">
                  <Plus className="h-4 w-4 mr-2" /> Creer un projet
                </Button>
              </div>
            </div>
          ) : (
            filteredProjects.map(p => (
              <ProjectCard key={p.id} project={p}
                onSync={handleSync} syncing={syncing}
                onEdit={setEditProject} onDelete={handleDelete}
                onScreenshot={handleTriggerScreenshot}
                onViewScreenshot={setScreenshotDialog}
                capturingScreenshot={capturingScreenshot} />
            ))
          )}
        </div>
      )}

      {/* DISCOVER TAB */}
      {tab === "discover" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[#94A3B8] text-sm">{repos.length} depots trouves sur GitHub</p>
            <Button size="sm" onClick={fetchRepos} disabled={loading}
              className="bg-[#0099CC] hover:bg-[#0088B5] text-white h-8" data-testid="refresh-repos-btn">
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
              Actualiser
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-[#0099CC]" />
            </div>
          ) : filteredRepos.length === 0 ? (
            <div className="text-center py-16 bg-[#151B2B] rounded-xl border border-[#2A3441]">
              <AlertTriangle className="h-10 w-10 text-[#F59E0B] mx-auto mb-3" />
              <p className="text-white font-semibold mb-2">Aucun depot trouve</p>
              <p className="text-[#94A3B8] text-sm max-w-md mx-auto">
                Verifiez que votre token GitHub (Fine-grained) dispose de l&apos;acces
                &ldquo;Repository access: All repositories&rdquo; avec la permission &ldquo;Contents: Read&rdquo;.
              </p>
            </div>
          ) : (
            filteredRepos.map(repo => (
              <div key={repo.full_name}
                className={`bg-[#151B2B] border rounded-xl p-4 transition-colors ${repo.imported ? "border-[#10B981]/30" : "border-[#2A3441] hover:border-[#0099CC]/20"}`}
                data-testid={`repo-${repo.name}`}>
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-white font-medium truncate">{repo.name}</h4>
                      {repo.private && <Lock className="h-3 w-3 text-[#F59E0B]" />}
                      {repo.imported && <Badge className="bg-[#10B981]/10 text-[#10B981] border-0 text-xs">Importe</Badge>}
                    </div>
                    <p className="text-[#64748b] text-xs truncate">{repo.description || "Pas de description"}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-[#94A3B8]">
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: LANG_COLORS[repo.language] || "#64748b" }} />
                        {repo.language || "N/A"}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {timeAgo(repo.pushed_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <GitBranch className="h-3 w-3" /> {repo.default_branch}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button size="sm" variant="outline" onClick={() => showDetails(repo.name)}
                      className="border-[#2A3441] text-[#94A3B8] hover:text-white h-8" data-testid={`details-${repo.name}`}>
                      <Eye className="h-3.5 w-3.5 mr-1" /> Details
                    </Button>
                    {!repo.imported ? (
                      <Button size="sm" onClick={() => handleImport(repo)} disabled={importing === repo.name}
                        className="bg-[#10B981] hover:bg-[#059669] text-white h-8" data-testid={`import-${repo.name}`}>
                        {importing === repo.name ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Download className="h-3.5 w-3.5 mr-1" />}
                        Importer
                      </Button>
                    ) : (
                      <Badge className="bg-[#10B981]/10 text-[#10B981] border-0 h-8 flex items-center">
                        <CheckCircle className="h-3.5 w-3.5 mr-1" /> Importe
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Repo Detail Dialog */}
      <Dialog open={!!detailDialog} onOpenChange={(o) => !o && setDetailDialog(null)}>
        <DialogContent className="bg-[#151B2B] border-[#2A3441] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <GitBranch className="h-5 w-5 text-[#0099CC]" />
              {detailDialog}
            </DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-[#0099CC]" />
            </div>
          ) : detailData && (
            <div className="space-y-4">
              <p className="text-[#94A3B8] text-sm">{detailData.description || "Pas de description"}</p>
              {detailData.detected_stack?.length > 0 && (
                <div>
                  <p className="text-xs text-[#64748b] uppercase tracking-wider mb-2">Stack detectee</p>
                  <div className="flex gap-2 flex-wrap">
                    {detailData.detected_stack.map(s => (
                      <Badge key={s} className="bg-[#0099CC]/10 text-[#0099CC] border-0">{s}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {detailData.languages && Object.keys(detailData.languages).length > 0 && (
                <div>
                  <p className="text-xs text-[#64748b] uppercase tracking-wider mb-2">Langages</p>
                  <div className="flex gap-2 flex-wrap">
                    {Object.entries(detailData.languages).map(([lang, bytes]) => (
                      <span key={lang} className="flex items-center gap-1 text-xs text-[#94A3B8] bg-[#0B0F19] px-2 py-1 rounded">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: LANG_COLORS[lang] || "#64748b" }} />
                        {lang} <span className="text-[#64748b]">({Math.round(bytes / 1024)}KB)</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {detailData.last_commit && (
                <div className="bg-[#0B0F19] rounded-lg p-3 border border-[#2A3441]">
                  <p className="text-xs text-[#64748b] uppercase tracking-wider mb-1">Dernier commit</p>
                  <p className="text-white text-sm">{detailData.last_commit.message}</p>
                  <p className="text-[#64748b] text-xs mt-1">
                    {detailData.last_commit.author} &middot; {detailData.last_commit.sha} &middot; {timeAgo(detailData.last_commit.date)}
                  </p>
                </div>
              )}
              {detailData.emergent_config && (
                <div className="bg-[#10B981]/5 border border-[#10B981]/20 rounded-lg p-3">
                  <p className="text-xs text-[#10B981] uppercase tracking-wider mb-2 flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" /> Config detectee
                  </p>
                  <pre className="text-xs text-[#94A3B8] overflow-x-auto whitespace-pre-wrap">
                    {JSON.stringify(detailData.emergent_config, null, 2)}
                  </pre>
                </div>
              )}
              <a href={detailData.html_url} target="_blank" rel="noopener noreferrer">
                <Button className="w-full bg-[#0B0F19] border border-[#2A3441] text-white hover:border-[#0099CC]/30">
                  <ExternalLink className="h-4 w-4 mr-2" /> Voir sur GitHub
                </Button>
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={!!editProject} onOpenChange={(o) => !o && setEditProject(null)}>
        <DialogContent className="bg-[#151B2B] border-[#2A3441] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Modifier le projet</DialogTitle>
          </DialogHeader>
          {editProject && (
            <EditProjectForm project={editProject} clients={clients}
              onSave={(updates) => handleUpdateProject(editProject.id, updates)}
              onCancel={() => setEditProject(null)} />
          )}
        </DialogContent>
      </Dialog>

      {/* Create Project Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-[#151B2B] border-[#2A3441] max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus className="h-5 w-5 text-[#0099CC]" /> Nouveau projet
            </DialogTitle>
          </DialogHeader>
          <CreateProjectForm clients={clients} onSave={handleManualCreate} onCancel={() => setShowCreateDialog(false)} />
        </DialogContent>
      </Dialog>

      {/* Screenshot Preview Dialog */}
      <Dialog open={!!screenshotDialog} onOpenChange={(o) => !o && setScreenshotDialog(null)}>
        <DialogContent className="bg-[#151B2B] border-[#2A3441] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Camera className="h-5 w-5 text-[#0099CC]" /> Capture d&apos;ecran
            </DialogTitle>
          </DialogHeader>
          {screenshotDialog && (
            <ScreenshotView project={screenshotDialog}
              onRegenerate={() => { handleTriggerScreenshot(screenshotDialog.id); setScreenshotDialog(null); }}
              onUpload={async (file) => {
                const fd = new FormData();
                fd.append("file", file);
                try {
                  await axios.post(`${API}/github/projects/${screenshotDialog.id}/screenshot/upload`, fd);
                  toast.success("Capture uploadee");
                  setScreenshotDialog(null);
                  fetchProjects();
                } catch { toast.error("Erreur upload"); }
              }} />
          )}
        </DialogContent>
      </Dialog>

      {/* Sync Logs Dialog */}
      <Dialog open={showSyncLogs} onOpenChange={setShowSyncLogs}>
        <DialogContent className="bg-[#151B2B] border-[#2A3441] max-w-xl max-h-[70vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Clock className="h-5 w-5 text-[#0099CC]" /> Journal de synchronisation
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3" data-testid="sync-logs-list">
            {syncLogs.length === 0 ? (
              <p className="text-[#64748b] text-sm text-center py-6">Aucun log de synchronisation</p>
            ) : syncLogs.map(log => (
              <div key={log.id} className={`rounded-lg p-3 border ${log.status === "completed" ? "border-[#10B981]/20 bg-[#10B981]/5" : log.status === "failed" ? "border-[#EF4444]/20 bg-[#EF4444]/5" : "border-[#F59E0B]/20 bg-[#F59E0B]/5"}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs font-semibold uppercase ${log.status === "completed" ? "text-[#10B981]" : log.status === "failed" ? "text-[#EF4444]" : "text-[#F59E0B]"}`}>
                    {log.status === "completed" ? "Terminee" : log.status === "failed" ? "Echouee" : "En cours"}
                  </span>
                  <span className="text-[10px] text-[#64748b]">
                    {log.started_at ? new Date(log.started_at).toLocaleString("fr-FR") : ""}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-xs text-[#94A3B8]">
                  <span>{log.repos_checked || 0} depots</span>
                  <span>{log.projects_updated || 0} mis a jour</span>
                  <span>{log.new_repos_detected || 0} nouveaux</span>
                  <span>{log.screenshots_triggered || 0} captures</span>
                </div>
                {log.errors?.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {log.errors.map((e, i) => (
                      <p key={i} className="text-[10px] text-[#EF4444]">{e}</p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============ PROJECT CARD ============ */
function ProjectCard({ project: p, onSync, syncing, onEdit, onDelete, onScreenshot, onViewScreenshot, capturingScreenshot }) {
  const st = STATUS_MAP[p.status] || STATUS_MAP.development;
  const StIcon = st.icon;
  const ssStatus = SCREENSHOT_STATUS[p.screenshot_status] || SCREENSHOT_STATUS.pending;
  const SsIcon = ssStatus.icon;
  const hasScreenshot = p.screenshot?.url;

  return (
    <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl overflow-hidden hover:border-[#0099CC]/20 transition-colors"
      data-testid={`project-${p.id}`}>
      <div className="flex">
        {/* Screenshot thumbnail */}
        <div className="w-40 min-h-[120px] bg-[#0B0F19] flex-shrink-0 relative cursor-pointer group border-r border-[#2A3441]"
          onClick={() => onViewScreenshot(p)} data-testid={`screenshot-thumb-${p.id}`}>
          {hasScreenshot ? (
            <img src={`${BASE_URL}${p.screenshot.url}`} alt={p.project_name}
              className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-1">
              <Monitor className="h-6 w-6 text-[#2A3441]" />
              <span className="text-[10px] text-[#64748b]">Pas de capture</span>
            </div>
          )}
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <Camera className="h-5 w-5 text-white" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-4 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h3 className="text-white font-semibold truncate">{p.project_name}</h3>
                <Badge className={`${st.color} border-0 text-[10px] px-1.5 py-0.5`}>
                  <StIcon className="h-2.5 w-2.5 mr-0.5" />{st.label}
                </Badge>
                {p.private && <Lock className="h-3 w-3 text-[#F59E0B]" />}
                {p.source === "manual" && <Badge className="bg-[#8B5CF6]/10 text-[#8B5CF6] border-0 text-[10px]">Manuel</Badge>}
              </div>

              <p className="text-[#64748b] text-xs truncate mb-2">{p.description || p.github_repo || "—"}</p>

              <div className="flex items-center gap-3 flex-wrap text-xs text-[#94A3B8]">
                {p.github_repo && (
                  <span className="flex items-center gap-1">
                    <GitBranch className="h-3 w-3" /> {p.default_branch}
                  </span>
                )}
                {p.last_commit && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {timeAgo(p.last_commit.date)}
                    <span className="text-[#64748b]">({p.last_commit.sha})</span>
                  </span>
                )}
                {p.client_name && (
                  <span className="flex items-center gap-1 text-[#10B981]">
                    <CheckCircle className="h-3 w-3" /> {p.client_name}
                  </span>
                )}
                {p.production_url && (
                  <a href={p.production_url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 text-[#0099CC] hover:underline">
                    <Globe className="h-3 w-3" /> Prod
                  </a>
                )}
                {p.preview_url && (
                  <a href={p.preview_url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 text-[#8B5CF6] hover:underline">
                    <Eye className="h-3 w-3" /> Preview
                  </a>
                )}
                <span className={`flex items-center gap-1 ${ssStatus.color}`}>
                  <SsIcon className={`h-3 w-3 ${p.screenshot_status === "capturing" ? "animate-spin" : ""}`} />
                  Capture: {ssStatus.label}
                </span>
              </div>

              {/* Technologies */}
              <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                {(p.technologies || []).slice(0, 6).map(lang => (
                  <span key={lang} className="flex items-center gap-1 text-[10px] text-[#94A3B8] bg-[#0B0F19] px-1.5 py-0.5 rounded">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: LANG_COLORS[lang] || "#64748b" }} />
                    {lang}
                  </span>
                ))}
              </div>

              {/* Progress bar */}
              {p.progress > 0 && (
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-1 bg-[#0B0F19] rounded-full overflow-hidden">
                    <div className="h-full bg-[#0099CC] rounded-full transition-all" style={{ width: `${p.progress}%` }} />
                  </div>
                  <span className="text-[10px] text-[#94A3B8]">{p.progress}%</span>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {p.github_repo && (
                <Button size="sm" variant="outline" onClick={() => onSync(p.id)}
                  disabled={syncing === p.id} title="Synchroniser"
                  className="border-[#2A3441] text-[#94A3B8] hover:text-white h-7 w-7 p-0" data-testid={`sync-${p.id}`}>
                  <RefreshCw className={`h-3 w-3 ${syncing === p.id ? "animate-spin" : ""}`} />
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => onScreenshot(p.id)}
                disabled={capturingScreenshot === p.id || p.screenshot_status === "capturing"} title="Capturer"
                className="border-[#2A3441] text-[#94A3B8] hover:text-white h-7 w-7 p-0" data-testid={`capture-${p.id}`}>
                <Camera className={`h-3 w-3 ${(capturingScreenshot === p.id || p.screenshot_status === "capturing") ? "animate-pulse" : ""}`} />
              </Button>
              <Button size="sm" variant="outline" onClick={() => onEdit(p)} title="Modifier"
                className="border-[#2A3441] text-[#94A3B8] hover:text-white h-7 w-7 p-0" data-testid={`edit-${p.id}`}>
                <Settings className="h-3 w-3" />
              </Button>
              {p.github_url && (
                <a href={p.github_url} target="_blank" rel="noopener noreferrer">
                  <Button size="sm" variant="outline" title="GitHub"
                    className="border-[#2A3441] text-[#94A3B8] hover:text-white h-7 w-7 p-0">
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                </a>
              )}
              <Button size="sm" variant="outline" onClick={() => onDelete(p.id)} title="Supprimer"
                className="border-[#2A3441] text-red-400 hover:text-red-300 hover:border-red-400/30 h-7 w-7 p-0" data-testid={`delete-${p.id}`}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============ CREATE PROJECT FORM ============ */
function CreateProjectForm({ clients, onSave, onCancel }) {
  const [form, setForm] = useState({
    project_name: "", client_id: "", client_name: "",
    production_url: "", preview_url: "", github_repository: "",
    project_type: "site_web", sector: "", status: "development",
    progress: 0, technologies: "", description: "", default_branch: "main",
    deadline: "",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!form.project_name.trim()) { toast.error("Le nom est requis"); return; }
    setSaving(true);
    const data = {
      ...form,
      progress: parseInt(form.progress) || 0,
      technologies: form.technologies ? form.technologies.split(",").map(t => t.trim()).filter(Boolean) : [],
    };
    if (form.client_id) {
      const c = clients.find(c => c.id === form.client_id);
      if (c) data.client_name = c.company_name;
    }
    await onSave(data);
    setSaving(false);
  };

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div className="space-y-3" data-testid="create-project-form">
      <div>
        <label className="text-[#94A3B8] text-xs mb-1 block">Nom du projet *</label>
        <Input value={form.project_name} onChange={e => set("project_name", e.target.value)}
          className="bg-[#0B0F19] border-[#2A3441] text-white" placeholder="Ex: GLOTECH DIGITAL OS"
          data-testid="create-project-name" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[#94A3B8] text-xs mb-1 block">Client</label>
          <select value={form.client_id || "none"} onChange={e => set("client_id", e.target.value === "none" ? "" : e.target.value)} className="w-full bg-[#0B0F19] border border-[#2A3441] text-white rounded-md px-3 py-2 text-sm" data-testid="create-client-select">
              <option value="none">Aucun client</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[#94A3B8] text-xs mb-1 block">Type de projet</label>
          <select value={form.project_type} onChange={e => set("project_type", e.target.value)} className="w-full bg-[#0B0F19] border border-[#2A3441] text-white rounded-md px-3 py-2 text-sm" data-testid="create-type-select">
              {PROJECT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="text-[#94A3B8] text-xs mb-1 block">URL de production</label>
        <Input value={form.production_url} onChange={e => set("production_url", e.target.value)}
          className="bg-[#0B0F19] border-[#2A3441] text-white" placeholder="https://www.exemple.fr"
          data-testid="create-prod-url" />
      </div>
      <div>
        <label className="text-[#94A3B8] text-xs mb-1 block">URL de preview</label>
        <Input value={form.preview_url} onChange={e => set("preview_url", e.target.value)}
          className="bg-[#0B0F19] border-[#2A3441] text-white" placeholder="https://preview.exemple.fr"
          data-testid="create-preview-url" />
      </div>
      <div>
        <label className="text-[#94A3B8] text-xs mb-1 block">Lien GitHub (facultatif)</label>
        <Input value={form.github_repository} onChange={e => set("github_repository", e.target.value)}
          className="bg-[#0B0F19] border-[#2A3441] text-white" placeholder="https://github.com/Glotech20/nom-du-projet"
          data-testid="create-github-url" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[#94A3B8] text-xs mb-1 block">Secteur</label>
          <Input value={form.sector} onChange={e => set("sector", e.target.value)}
            className="bg-[#0B0F19] border-[#2A3441] text-white" placeholder="Ex: immobilier"
            data-testid="create-sector" />
        </div>
        <div>
          <label className="text-[#94A3B8] text-xs mb-1 block">Statut</label>
          <select value={form.status} onChange={e => set("status", e.target.value)} className="w-full bg-[#0B0F19] border border-[#2A3441] text-white rounded-md px-3 py-2 text-sm" data-testid="create-status-select">
              <option value="development">Developpement</option>
              <option value="staging">Staging</option>
              <option value="production">Production</option>
              <option value="archived">Archive</option>
          </select>
        </div>
      </div>
      <div>
        <label className="text-[#94A3B8] text-xs mb-1 block">Technologies (separees par virgule)</label>
        <Input value={form.technologies} onChange={e => set("technologies", e.target.value)}
          className="bg-[#0B0F19] border-[#2A3441] text-white" placeholder="React, FastAPI, MongoDB"
          data-testid="create-technologies" />
      </div>
      <div>
        <label className="text-[#94A3B8] text-xs mb-1 block">Progression ({form.progress}%)</label>
        <input type="range" min="0" max="100" step="5" value={form.progress}
          onChange={e => set("progress", e.target.value)}
          className="w-full accent-[#0099CC]" data-testid="create-progress" />
      </div>
      <div>
        <label className="text-[#94A3B8] text-xs mb-1 block">Date de livraison prevue</label>
        <Input type="date" value={form.deadline} onChange={e => set("deadline", e.target.value)}
          className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="create-deadline" />
      </div>
      <div>
        <label className="text-[#94A3B8] text-xs mb-1 block">Description</label>
        <Input value={form.description} onChange={e => set("description", e.target.value)}
          className="bg-[#0B0F19] border-[#2A3441] text-white" placeholder="Description du projet"
          data-testid="create-description" />
      </div>
      <div className="flex gap-2 pt-2">
        <Button onClick={handleSubmit} disabled={saving}
          className="flex-1 bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="save-create-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
          Creer le projet
        </Button>
        <Button variant="outline" onClick={onCancel} className="border-[#2A3441] text-[#94A3B8]">Annuler</Button>
      </div>
    </div>
  );
}

/* ============ EDIT PROJECT FORM ============ */
function EditProjectForm({ project, clients, onSave, onCancel }) {
  const [form, setForm] = useState({
    project_name: project.project_name || "",
    client_id: project.client_id || "",
    status: project.status || "development",
    progress: project.progress || 0,
    production_url: project.production_url || "",
    preview_url: project.preview_url || "",
    sector: project.sector || "",
    technologies: (project.technologies || []).join(", "),
    deadline: project.deadline ? (project.deadline.includes("T") ? project.deadline.split("T")[0] : project.deadline) : "",
  });

  const handleSave = () => {
    const data = { ...form, progress: parseInt(form.progress) || 0 };
    if (typeof data.technologies === "string") {
      data.technologies = data.technologies.split(",").map(t => t.trim()).filter(Boolean);
    }
    onSave(data);
  };

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div className="space-y-3" data-testid="edit-project-form">
      <div>
        <label className="text-[#94A3B8] text-xs mb-1 block">Nom du projet</label>
        <Input value={form.project_name} onChange={e => set("project_name", e.target.value)}
          className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="edit-project-name" />
      </div>
      <div>
        <label className="text-[#94A3B8] text-xs mb-1 block">Client</label>
        <Select value={form.client_id || "none"} onValueChange={v => set("client_id", v === "none" ? "" : v)}>
          <SelectTrigger className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="edit-client-select">
            <SelectValue placeholder="Aucun client" />
          </SelectTrigger>
          <SelectContent className="bg-[#151B2B] border-[#2A3441]">
            <SelectItem value="none">Aucun client</SelectItem>
            {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-[#94A3B8] text-xs mb-1 block">Statut</label>
        <Select value={form.status} onValueChange={v => set("status", v)}>
          <SelectTrigger className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="edit-status-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#151B2B] border-[#2A3441]">
            <SelectItem value="development">Developpement</SelectItem>
            <SelectItem value="staging">Staging</SelectItem>
            <SelectItem value="production">Production</SelectItem>
            <SelectItem value="archived">Archive</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-[#94A3B8] text-xs mb-1 block">Progression ({form.progress}%)</label>
        <input type="range" min="0" max="100" step="5" value={form.progress}
          onChange={e => set("progress", parseInt(e.target.value))}
          className="w-full accent-[#0099CC]" data-testid="edit-progress" />
      </div>
      <div>
        <label className="text-[#94A3B8] text-xs mb-1 block">URL Production</label>
        <Input value={form.production_url} onChange={e => set("production_url", e.target.value)}
          className="bg-[#0B0F19] border-[#2A3441] text-white" placeholder="https://..." data-testid="edit-prod-url" />
      </div>
      <div>
        <label className="text-[#94A3B8] text-xs mb-1 block">URL Preview</label>
        <Input value={form.preview_url} onChange={e => set("preview_url", e.target.value)}
          className="bg-[#0B0F19] border-[#2A3441] text-white" placeholder="https://..." data-testid="edit-preview-url" />
      </div>
      <div>
        <label className="text-[#94A3B8] text-xs mb-1 block">Technologies</label>
        <Input value={form.technologies} onChange={e => set("technologies", e.target.value)}
          className="bg-[#0B0F19] border-[#2A3441] text-white" placeholder="React, FastAPI, MongoDB"
          data-testid="edit-technologies" />
      </div>
      <div>
        <label className="text-[#94A3B8] text-xs mb-1 block">Date de livraison prevue</label>
        <Input type="date" value={form.deadline} onChange={e => set("deadline", e.target.value)}
          className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="edit-deadline" />
      </div>
      <div className="flex gap-2 pt-2">
        <Button onClick={handleSave} className="flex-1 bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="save-edit-btn">
          Enregistrer
        </Button>
        <Button variant="outline" onClick={onCancel} className="border-[#2A3441] text-[#94A3B8]">Annuler</Button>
      </div>
    </div>
  );
}

/* ============ SCREENSHOT VIEW ============ */
function ScreenshotView({ project, onRegenerate, onUpload }) {
  const fileRef = useRef(null);
  const hasScreenshot = project.screenshot?.url;
  const ssStatus = SCREENSHOT_STATUS[project.screenshot_status] || SCREENSHOT_STATUS.pending;

  return (
    <div className="space-y-4" data-testid="screenshot-view">
      <div className="flex items-center gap-2 text-sm">
        <span className={`flex items-center gap-1 ${ssStatus.color}`}>
          <ssStatus.icon className={`h-4 w-4 ${project.screenshot_status === "capturing" ? "animate-spin" : ""}`} />
          Statut: {ssStatus.label}
        </span>
        {project.screenshot?.captured_at && (
          <span className="text-[#64748b] text-xs">({timeAgo(project.screenshot.captured_at)})</span>
        )}
      </div>

      {hasScreenshot ? (
        <div className="relative rounded-lg overflow-hidden border border-[#2A3441]">
          <img src={`${BASE_URL}${project.screenshot.url}`} alt={project.project_name}
            className="w-full h-auto" />
        </div>
      ) : (
        <div className="flex items-center justify-center py-12 bg-[#0B0F19] rounded-lg border border-[#2A3441]">
          <div className="text-center">
            <Image className="h-10 w-10 text-[#2A3441] mx-auto mb-2" />
            <p className="text-[#64748b] text-sm">Aucune capture disponible</p>
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <Button onClick={onRegenerate} disabled={project.screenshot_status === "capturing"}
          className="flex-1 bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="regenerate-screenshot-btn">
          <Camera className="h-4 w-4 mr-2" />
          {hasScreenshot ? "Regenerer la capture" : "Capturer"}
        </Button>
        <Button variant="outline" onClick={() => fileRef.current?.click()}
          className="border-[#2A3441] text-[#94A3B8] hover:text-white" data-testid="upload-screenshot-btn">
          <Upload className="h-4 w-4 mr-2" /> Upload
        </Button>
        <input type="file" ref={fileRef} className="hidden" accept="image/*"
          onChange={e => { if (e.target.files?.[0]) onUpload(e.target.files[0]); }} />
      </div>

      {!project.production_url && !project.preview_url && (
        <p className="text-[#F59E0B] text-xs flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" /> Ajoutez une URL au projet pour activer la capture automatique
        </p>
      )}
    </div>
  );
}
